// Charts.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./Charts.css";

/** ---------- Shared utilities ---------- */
const useUniqueId = (prefix = "charts") => {
    const id = useMemo(() => `${prefix}-${Math.random().toString(36).slice(2, 9)}`, []);
    return id;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const getMinMax = (arr, key = "y") => {
    if (!arr?.length) return [0, 1];
    let min = Infinity, max = -Infinity;
    for (const d of arr) {
        const v = Number(d[key]);
        if (!Number.isFinite(v)) continue;
        if (v < min) min = v;
        if (v > max) max = v;
    }
    if (min === Infinity) return [0, 1];
    if (min === max) return [min - 1, max + 1];
    return [min, max];
};

const niceTicks = (min, max, count = 5) => {
    if (min === max) return [min];
    const span = max - min;
    const step = Math.pow(10, Math.floor(Math.log10(span / count)));
    const err = (span / count) / step;
    const factor = err >= 7.5 ? 10 : err >= 3 ? 5 : err >= 1.5 ? 2 : 1;
    const niceStep = factor * step;
    const niceMin = Math.floor(min / niceStep) * niceStep;
    const niceMax = Math.ceil(max / niceStep) * niceStep;
    const ticks = [];
    for (let v = niceMin; v <= niceMax + 1e-9; v += niceStep) ticks.push(v);
    return ticks;
};

/** ---------- Tooltip host ---------- */
function useTooltip() {
    const [tip, setTip] = useState(null); // {x,y,content}
    const show = useCallback((x, y, content) => setTip({ x, y, content }), []);
    const hide = useCallback(() => setTip(null), []);
    return { tip, show, hide };
}

function ChartContainer({ width, height, children, tooltip }) {
    return (
        <div className="charts-container" style={{ width, height }}>
            {children}
            {tooltip?.tip && (
                <div
                    className="charts-tooltip"
                    style={{ left: tooltip.tip.x, top: tooltip.tip.y }}
                >
                    {tooltip.tip.content}
                </div>
            )}
        </div>
    );
}

/** ---------- Base SVG Wrapper ---------- */
function ChartSvg({ width = 600, height = 320, padding = { top: 24, right: 24, bottom: 36, left: 48 }, children, title, onMouseLeave }) {
    const w = width, h = height;
    const innerW = w - padding.left - padding.right;
    const innerH = h - padding.top - padding.bottom;
    return (
        <svg
            className="charts-root"
            width={w}
            height={h}
            viewBox={`0 0 ${w} ${h}`}
            role="img"
            aria-label={ "chart"}
            onMouseLeave={onMouseLeave}
        >
            <g transform={`translate(${padding.left},${padding.top})`}>
                {children({ innerW, innerH, padding })}
            </g>
        </svg>
    );
}

/** ---------- LineChart (animated + crosshair + tooltip) ---------- */
export function LineChart({
    data = [],
    width = 640,
    height = 320,
    xTickCount = 6,
    yTickCount = 5,
    showDots = true,
    smooth = true,
}) {
    const pad = { top: 24, right: 24, bottom: 40, left: 56 };
    const ids = useUniqueId("line");
    const tooltip = useTooltip();
    const [hoverIdx, setHoverIdx] = useState(null);
    const pathRef = useRef(null);

    const numericX = data.map((d, i) => ({ ...d, _x: i }));
    const [minY, maxY] = getMinMax(numericX, "y");

    useEffect(() => {
        // line draw animation
        const p = pathRef.current;
        if (!p) return;
        const len = p.getTotalLength?.() ?? 0;
        p.style.setProperty("--path-len", `${len}`);
        p.classList.remove("charts-line-animate"); // restart animation
        void p.getBoundingClientRect();
        p.classList.add("charts-line-animate");
    }, [data, smooth, width, height]);

    return (
        <ChartContainer width={width} height={height} tooltip={tooltip}>
            <ChartSvg width={width} height={height} padding={pad} onMouseLeave={() => { tooltip.hide(); setHoverIdx(null); }}>
                {({ innerW, innerH }) => {
                    const xScale = (i) => (numericX.length <= 1 ? innerW / 2 : (i / (numericX.length - 1)) * innerW);
                    const yScale = (v) => innerH - ((v - minY) / (maxY - minY)) * innerH;

                    const yTicks = niceTicks(minY, maxY, yTickCount);
                    const xTicksIdx = Array.from({ length: Math.min(xTickCount, numericX.length) }, (_, i) =>
                        Math.round((i * (numericX.length - 1)) / Math.max(1, xTickCount - 1))
                    );

                    const points = numericX.map((d, i) => [xScale(i), yScale(d.y)]);
                    const pathD = smooth
                        ? points.reduce((acc, p, i, arr) => {
                            if (i === 0) return `M ${p[0]},${p[1]}`;
                            const p0 = arr[i - 1];
                            const cx = (p0[0] + p[0]) / 2;
                            return `${acc} Q ${p0[0]},${p0[1]} ${cx},${(p0[1] + p[1]) / 2} T ${p[0]},${p[1]}`;
                        }, "")
                        : points.map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`)).join(" ");

                    const handleMove = (e) => {
                        const { left, top } = e.currentTarget.getBoundingClientRect();
                        const mx = e.clientX - left - pad.left;
                        const my = e.clientY - top - pad.top;
                        if (mx < 0 || mx > innerW || my < 0 || my > innerH) { setHoverIdx(null); tooltip.hide(); return; }
                        // closest point by index (uniform x)
                        const idx = clamp(Math.round((mx / innerW) * (numericX.length - 1)), 0, numericX.length - 1);
                        setHoverIdx(idx);
                        const px = xScale(idx), py = yScale(numericX[idx].y);
                        tooltip.show(px + pad.left + 8, py + pad.top - 28,
                            <div>
                                <div className="charts-tip-title">{String(numericX[idx].x)}</div>
                                <div className="charts-tip-value">{numericX[idx].y}</div>
                            </div>
                        );
                    };

                    return (
                        <>
                            <defs>
                                <linearGradient id={`${ids}-grad`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="var(--calendar-task-slot-border-color)" stopOpacity="0.35" />
                                    <stop offset="100%" stopColor="var(--calendar-task-slot-border-color)" stopOpacity="0" />
                                </linearGradient>
                                <clipPath id={`${ids}-clip`}>
                                    <rect x="0" y="0" width={innerW} height={innerH} rx="8" ry="8" />
                                </clipPath>
                            </defs>

                            {yTicks.map((t, i) => <line key={i} x1={0} x2={innerW} y1={yScale(t)} y2={yScale(t)} className="charts-grid" />)}
                            {xTicksIdx.map((idx) => <line key={idx} x1={xScale(idx)} x2={xScale(idx)} y1={0} y2={innerH} className="charts-grid-x" />)}

                            <line x1={0} y1={innerH} x2={innerW} y2={innerH} className="charts-axis" />
                            <line x1={0} y1={0} x2={0} y2={innerH} className="charts-axis" />

                            {yTicks.map((t, i) => <text key={i} x={-8} y={yScale(t)} className="charts-label charts-label--y">{t}</text>)}
                            {xTicksIdx.map((idx, i) => (
                                <text key={i} x={xScale(idx)} y={innerH + 24} className="charts-label charts-label--x">
                                    {String(numericX[idx]?.x ?? idx)}
                                </text>
                            ))}

                            {/* Hover crosshair vertical */}
                            {hoverIdx != null && (
                                <line x1={xScale(hoverIdx)} x2={xScale(hoverIdx)} y1={0} y2={innerH} className="charts-crosshair" />
                            )}

                            {/* Area fill */}
                            <path
                                d={`${pathD} L ${innerW},${innerH} L 0,${innerH} Z`}
                                fill={`url(#${ids}-grad)`}
                                clipPath={`url(#${ids}-clip)`}
                                className="charts-area-fade"
                            />

                            {/* Line (animated) */}
                            <path ref={pathRef} d={pathD} className="charts-line charts-line-animate" fill="none" />

                            {/* Dots */}
                            {showDots && numericX.map((d, i) => {
                                const cx = xScale(i), cy = yScale(d.y);
                                const active = i === hoverIdx;
                                return (
                                    <circle
                                        key={i}
                                        cx={cx}
                                        cy={cy}
                                        r={active ? 5 : 3.5}
                                        className={`charts-dot${active ? " charts-dot--active" : ""}`}
                                    />
                                );
                            })}

                            {/* Mouse layer */}
                            <rect
                                className="charts-hitbox"
                                x="0" y="0" width={innerW} height={innerH}
                                onMouseMove={handleMove}
                                onMouseEnter={handleMove}
                            />
                        </>
                    );
                }}
            </ChartSvg>
        </ChartContainer>
    );
}

/** ---------- BarChart (hover lift + value bubble) ---------- */
export function BarChart({
    data = [],
    width = 640,
    height = 320,
    gap = 0.18,
    rounded = 6,
    valueFormatter = (v) => v
}) {
    const pad = { top: 24, right: 16, bottom: 48, left: 56 };
    const tooltip = useTooltip();
    const [min, max] = getMinMax(data.map((d) => ({ y: d.value })), "y");
    const minY = Math.min(0, min);
    const maxY = Math.max(0, max);

    return (
        <ChartContainer width={width} height={height} tooltip={tooltip}>
            <ChartSvg width={width} height={height} padding={pad} onMouseLeave={tooltip.hide}>
                {({ innerW, innerH }) => {
                    const n = data.length || 1;
                    const fullBand = innerW / n;
                    const barW = fullBand * (1 - gap);

                    const x = (i) => i * fullBand + (fullBand - barW) / 2;
                    const y = (v) => innerH - ((v - minY) / (maxY - minY || 1)) * innerH;

                    const yTicks = niceTicks(minY, maxY, 5);

                    return (
                        <>
                            {yTicks.map((t, i) => <line key={i} x1={0} x2={innerW} y1={y(t)} y2={y(t)} className="charts-grid" />)}

                            <line x1={0} y1={innerH} x2={innerW} y2={innerH} className="charts-axis" />
                            <line x1={0} y1={0} x2={0} y2={innerH} className="charts-axis" />

                            {yTicks.map((t, i) => (
                                <text key={i} x={-8} y={y(t)} className="charts-label charts-label--y">
                                    {valueFormatter(t)}
                                </text>
                            ))}

                            {data.map((d, i) => {
                                const val = Number(d.value) || 0;
                                const x0 = x(i);
                                const y0 = y(Math.max(0, val));
                                const yBase = y(0);
                                const h = Math.abs(yBase - y(val));
                                const cx = x0 + barW / 2;
                                const cy = y(val) - 10;

                                const onEnter = (e) => {
                                    tooltip.show(cx + pad.left, Math.min(cy, innerH - 24) + pad.top,
                                        <div>
                                            <div className="charts-tip-title">{d.label}</div>
                                            <div className="charts-tip-value">{valueFormatter(val)}</div>
                                        </div>
                                    );
                                };

                                return (
                                    <g key={i} className="charts-bar-group" onMouseEnter={onEnter}>
                                        <rect
                                            x={x0}
                                            y={y0}
                                            width={barW}
                                            height={h}
                                            rx={rounded}
                                            className="charts-bar"
                                        />
                                        <text x={x0 + barW / 2} y={innerH + 24} className="charts-label charts-label--x">
                                            {d.label}
                                        </text>
                                    </g>
                                );
                            })}
                        </>
                    );
                }}
            </ChartSvg>
        </ChartContainer>
    );
}

/** ---------- AreaChart (draw + fade + tooltip) ---------- */
export function AreaChart({
    data = [],
    width = 640,
    height = 320,
    xTickCount = 6,
    yTickCount = 5,
}) {
    const pad = { top: 24, right: 24, bottom: 40, left: 56 };
    const ids = useUniqueId("area");
    const tooltip = useTooltip();
    const [hoverIdx, setHoverIdx] = useState(null);
    const pathRef = useRef(null);

    const series = data.map((d, i) => ({ ...d, _x: i }));
    const [minY, maxY] = getMinMax(series, "y");

    useEffect(() => {
        const p = pathRef.current;
        if (!p) return;
        const len = p.getTotalLength?.() ?? 0;
        p.style.setProperty("--path-len", `${len}`);
        p.classList.remove("charts-line-animate");
        void p.getBoundingClientRect();
        p.classList.add("charts-line-animate");
    }, [data, width, height]);

    return (
        <ChartContainer width={width} height={height} tooltip={tooltip}>
            <ChartSvg width={width} height={height} padding={pad} onMouseLeave={() => { tooltip.hide(); setHoverIdx(null); }}>
                {({ innerW, innerH }) => {
                    const xScale = (i) => (series.length <= 1 ? innerW / 2 : (i / (series.length - 1)) * innerW);
                    const yScale = (v) => innerH - ((v - minY) / (maxY - minY)) * innerH;

                    const yTicks = niceTicks(minY, maxY, yTickCount);
                    const xTicksIdx = Array.from({ length: Math.min(xTickCount, series.length) }, (_, i) =>
                        Math.round((i * (series.length - 1)) / Math.max(1, xTickCount - 1))
                    );

                    const pts = series.map((d, i) => [xScale(i), yScale(d.y)]);
                    const dPath = pts.map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`)).join(" ");

                    const handleMove = (e) => {
                        const { left, top } = e.currentTarget.getBoundingClientRect();
                        const mx = e.clientX - left - pad.left;
                        if (mx < 0 || mx > innerW) { setHoverIdx(null); tooltip.hide(); return; }
                        const idx = clamp(Math.round((mx / innerW) * (series.length - 1)), 0, series.length - 1);
                        setHoverIdx(idx);
                        const px = xScale(idx), py = yScale(series[idx].y);
                        tooltip.show(px + pad.left + 8, py + pad.top - 28,
                            <div>
                                <div className="charts-tip-title">{String(series[idx].x)}</div>
                                <div className="charts-tip-value">{series[idx].y}</div>
                            </div>
                        );
                    };

                    return (
                        <>
                            <defs>
                                <linearGradient id={`${ids}-fill`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="var(--calendar-completed-task-slot-border-color)" stopOpacity="0.32" />
                                    <stop offset="100%" stopColor="var(--calendar-completed-task-slot-border-color)" stopOpacity="0" />
                                </linearGradient>
                            </defs>

                            {yTicks.map((t, i) => <line key={i} x1={0} x2={innerW} y1={yScale(t)} y2={yScale(t)} className="charts-grid" />)}
                            {xTicksIdx.map((idx) => <line key={idx} x1={xScale(idx)} x2={xScale(idx)} y1={0} y2={innerH} className="charts-grid-x" />)}

                            <line x1={0} y1={innerH} x2={innerW} y2={innerH} className="charts-axis" />
                            <line x1={0} y1={0} x2={0} y2={innerH} className="charts-axis" />

                            {yTicks.map((t, i) => <text key={i} x={-8} y={yScale(t)} className="charts-label charts-label--y">{t}</text>)}
                            {xTicksIdx.map((idx, i) => (
                                <text key={i} x={xScale(idx)} y={innerH + 24} className="charts-label charts-label--x">
                                    {String(series[idx]?.x ?? idx)}
                                </text>
                            ))}

                            {hoverIdx != null && (
                                <line x1={xScale(hoverIdx)} x2={xScale(hoverIdx)} y1={0} y2={innerH} className="charts-crosshair" />
                            )}

                            <path d={`${dPath} L ${innerW},${innerH} L 0,${innerH} Z`} fill={`url(#${ids}-fill)`} className="charts-area-fade" />
                            <path ref={pathRef} d={dPath} className="charts-line charts-line--area charts-line-animate" fill="none" />

                            <rect className="charts-hitbox" x="0" y="0" width={innerW} height={innerH}
                                onMouseMove={handleMove} onMouseEnter={handleMove} />
                        </>
                    );
                }}
            </ChartSvg>
        </ChartContainer>
    );
}

/** ---------- DonutChart (slice pop-out + legend highlight + tooltip) ---------- */
export function DonutChart({
    data = [],
    width = 480,
    height = 320,
    innerRadiusRatio = 0.62,
    showLegend = true,
    valueFormatter = (v) => v
}) {
    const ids = useUniqueId("donut");
    const tooltip = useTooltip();
    const [hover, setHover] = useState(-1);

    const size = Math.min(width, height);
    const r = (size / 2) * 0.9;
    const cx = width / 2;
    const cy = height / 2;
    const innerR = clamp(r * innerRadiusRatio, 0, r - 2);
    const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0) || 1;

    const palette = [
        "var(--calendar-task-slot-border-color)",
        "var(--calendar-completed-task-slot-border-color)",
        "var(--chatbar-selected-tab-font-color)",
        "var(--kronos-timer-clock-font-color)",
        "var(--calendar-closing-task-slot-border-color)",
        "var(--sh-sidebar-active-server-bg)",
        "var(--note-card-title-color)"
    ];

    let start = -Math.PI / 2;
    const arcs = data.map((d, i) => {
        const frac = (Number(d.value) || 0) / total;
        const angle = frac * Math.PI * 2;
        const end = start + angle;
        const largeArc = angle > Math.PI ? 1 : 0;

        const mid = (start + end) / 2;
        const offset = i === hover ? 8 : 0;
        const tx = Math.cos(mid) * offset;
        const ty = Math.sin(mid) * offset;

        const x1 = cx + r * Math.cos(start);
        const y1 = cy + r * Math.sin(start);
        const x2 = cx + r * Math.cos(end);
        const y2 = cy + r * Math.sin(end);

        const xi2 = cx + innerR * Math.cos(end);
        const yi2 = cy + innerR * Math.sin(end);
        const xi1 = cx + innerR * Math.cos(start);
        const yi1 = cy + innerR * Math.sin(start);

        const dPath = [
            `M ${x1} ${y1}`,
            `A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`,
            `L ${xi2} ${yi2}`,
            `A ${innerR} ${innerR} 0 ${largeArc} 0 ${xi1} ${yi1}`,
            "Z",
        ].join(" ");

        start = end;
        return { dPath, color: palette[i % palette.length], label: d.label, value: d.value, tx, ty };
    });

    const centerValue = valueFormatter(total);

    // --- NEW: container-relative mouse position helper ---
    const getLocalPos = (evt) => {
        const svg = evt.currentTarget.ownerSVGElement;
        const container = svg?.closest(".charts-container");
        const rect = container?.getBoundingClientRect();
        if (!rect) return { x: 0, y: 0 };
        return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
    };

    // --- UPDATED: use mouse coords relative to container for tooltip ---
    const onSliceMove = (i, a) => (e) => {
        setHover(i);
        const { x, y } = getLocalPos(e);
        tooltip.show(
            x, // container-relative X
            y, // container-relative Y
            <div>
                <div className="charts-tip-title">{a.label}</div>
                <div className="charts-tip-value">{valueFormatter(a.value)}</div>
            </div>
        );
    };

    const onSliceLeave = () => { setHover(-1); tooltip.hide(); };

    return (
        <div className="charts-donut-wrap" style={{ width, height }}>
            <ChartContainer width={width} height={height} tooltip={tooltip}>
                <svg className="charts-root" width={width} height={height} role="img" onMouseLeave={onSliceLeave}>
                    <defs>
                        <filter id={`${ids}-shadow`} x="-50%" y="-50%" width="200%" height="200%">
                            <feDropShadow dx="0" dy="0" stdDeviation="2" floodOpacity="0.2" />
                        </filter>
                    </defs>

                    <g filter={`url(#${ids}-shadow)`}>
                        {arcs.map((a, i) => (
                            <g
                                key={i}
                                style={{ transform: `translate(${a.tx}px, ${a.ty}px)` }}
                                className={`charts-slice${hover !== -1 && hover !== i ? " charts-slice--dim" : ""}`}
                            >
                                <path
                                    d={a.dPath}
                                    fill={a.color}
                                    onMouseEnter={onSliceMove(i, a)}
                                    onMouseMove={onSliceMove(i, a)}   // <— NEW: follow cursor
                                    onMouseLeave={onSliceLeave}
                                />
                            </g>
                        ))}
                    </g>

                    <circle cx={cx} cy={cy} r={innerR} fill="var(--tab-bg)" />
                    <text x={cx} y={cy - 2} textAnchor="middle" className="charts-donut-total">{centerValue}</text>
                    <text x={cx} y={cy + 18} textAnchor="middle" className="charts-donut-sub">Toplam</text>
                </svg>
            </ChartContainer>

            {showLegend && (
                <div className="charts-legend">
                    {data.map((d, i) => (
                        <div
                            className={`charts-legend-item${hover === i ? " charts-legend-item--active" : ""}`}
                            key={i}
                            onMouseEnter={() => setHover(i)}
                            onMouseLeave={() => setHover(-1)}
                        >
                            <span className="charts-legend-dot" style={{ background: palette[i % palette.length] }} />
                            <span className="charts-legend-label">{d.label}</span>
                            <span className="charts-legend-value">{valueFormatter(d.value)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}